import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { usePartner } from '../contexts/PartnerContext';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { loading, error, isAuthenticated, isPartnerApproved, forceCheckAuth } = usePartner();
  const [isProcessingToken, setIsProcessingToken] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      if (isPartnerApproved) {
        navigate('/dashboard');
      } else {
        navigate('/');
      }
      return;
    }

    // Проверяем ошибки в URL
    const error = searchParams.get('error');
    if (error) {
      let errorMessage = 'Ошибка авторизации';
      switch (error) {
        case 'auth_failed':
          errorMessage = 'Ошибка авторизации через Steam. Попробуйте еще раз.';
          break;
        default:
          errorMessage = 'Неизвестная ошибка авторизации. Попробуйте еще раз.';
          break;
      }
      // Здесь можно добавить отображение ошибки
      // Auth error
    }

    // Проверяем, есть ли токен в URL (после Steam авторизации)
    const token = searchParams.get('token');
    const steamId = searchParams.get('steamId');
    
    if (token && steamId && !isProcessingToken) {
      // Обрабатываем токен асинхронно
      handleTokenFromUrl(token);
    }
  }, [isAuthenticated, isPartnerApproved, navigate, searchParams, isProcessingToken]);

  const handleTokenFromUrl = async (token: string) => {
    try {
      setIsProcessingToken(true);
      
      // Сохраняем токен
      localStorage.setItem('partner_token', token);
      
      // Принудительно проверяем авторизацию и ждем результата
      await forceCheckAuth();
      
      // После успешной проверки перенаправляем на dashboard
      // isAuthenticated обновится автоматически и сработает redirect в следующем useEffect
    } catch (error) {
      // Token processing error
      // В случае ошибки очищаем токен
      localStorage.removeItem('partner_token');
    } finally {
      setIsProcessingToken(false);
    }
  };

  const handleSteamAuth = () => {
    // Перенаправляем на Steam авторизацию для партнеров
    const steamAuthUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:3021'}/partner/auth/steam`;
    window.location.href = steamAuthUrl;
  };

  if (loading || isProcessingToken) {
    return (
      <div className="min-h-screen bg-tesla-dark flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center"
        >
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-tesla-red mx-auto mb-4"></div>
          <p className="text-white">{isProcessingToken ? 'Обработка авторизации...' : 'Загрузка...'}</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-tesla-dark flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="max-w-md w-full"
      >
        <div className="tesla-card p-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-white mb-2">CSFade Partner</h1>
            <p className="text-gray-400">Войдите в панель управления партнера</p>
          </div>

          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-red-500/20 border border-red-500/50 rounded-lg p-4 mb-6"
            >
              <p className="text-red-400 text-sm">{error}</p>
            </motion.div>
          )}

          <div className="space-y-4">
            <button
              onClick={handleSteamAuth}
              disabled={loading || isProcessingToken}
              className="w-full tesla-button flex items-center justify-center space-x-3 py-3 px-4"
            >
              <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 0C5.37 0 0 5.37 0 12s5.37 12 12 12 12-5.37 12-12S18.63 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z"/>
              </svg>
              <span>Войти через Steam</span>
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Login;